import { useCallback, useState } from 'react';

/**
 * A hook that tracks updates and provides callbacks to undo/redo.
 */
function useUndoableUpdate<T>(
  initialState: T,
  updateCallback: (arg: T) => void,
): {
  handleChangeWithUndoRedo: (arg: T) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  currentValue: T;
} {
  const [historyList, setHistoryList] = useState<T[]>([initialState]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const handleChangeWithUndoRedo = (arg: T) => {
    updateCallback(arg);
    setHistoryList([...historyList.slice(0, historyIndex + 1), arg]);
    setHistoryIndex(historyIndex + 1);
  };

  const undo = useCallback(() => {
    // cannot undo if there is no prior history
    if (historyIndex < 1) return;

    const nextHistoryIndex = historyIndex - 1;
    setHistoryIndex(nextHistoryIndex);
    updateCallback(historyList[nextHistoryIndex]);
  }, [updateCallback, historyList, historyIndex]);

  const redo = useCallback(() => {
    // cannot redo if we are at the latest version
    if (historyIndex >= historyList.length - 1) return;

    const nextHistoryIndex = historyIndex + 1;
    setHistoryIndex(nextHistoryIndex);
    updateCallback(historyList[nextHistoryIndex]);
  }, [updateCallback, historyList, historyIndex]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < historyList.length - 1;

  return {
    handleChangeWithUndoRedo,
    undo,
    redo,
    canUndo,
    canRedo,
    currentValue: historyList[historyIndex],
  };
}

export default useUndoableUpdate;
